
import { CalendarClock, CheckCircle2, Mail, MessageSquare } from "lucide-react";
import type { Activity } from "./ActivityTypes";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/** Icon for activity type */
export const getActivityIcon = (type: Activity['type']) => {
  switch (type) {
    case 'meeting':
      return <CalendarClock className="h-4 w-4" />;
    case 'email':
      return <Mail className="h-4 w-4" />;
    case 'followup':
      return <MessageSquare className="h-4 w-4" />;
    case 'task':
      return <CheckCircle2 className="h-4 w-4" />;
  }
};

export const getPriorityColor = (priority: Activity['priority']) => {
  switch (priority) {
    case 'high':
      return 'text-red-500';
    case 'medium':
      return 'text-yellow-500';
    case 'low':
      return 'text-green-500';
  }
};

/**
 * Checks if a string is a valid UUID
 */
export const isValidUuid = (id: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

/**
 * Navigation handler for activities.
 * Call with: `const handleNav = useActivityNavigation(); ... handleNav(activity)`
 */
export const useActivityNavigation = () => {
  const navigate = useNavigate();
  return (activity: Activity) => {
    if (activity.related_entity) {
      const entityId = activity.related_entity.id;
      
      // Check if the ID is a valid UUID before navigating
      if (!isValidUuid(entityId)) {
        console.error(`Invalid ID format for navigation: ${entityId}`);
        toast.error("Cannot navigate to this item - invalid ID format");
        return;
      }
      
      switch (activity.related_entity.type) {
        case 'candidate':
          navigate(`/candidates/${entityId}`);
          break;
        case 'job':
          navigate(`/jobs/${entityId}`);
          break;
        case 'client':
          navigate(`/clients/${entityId}`);
          break;
      }
    } else {
      switch (activity.type) {
        case 'meeting':
          navigate('/calendar');
          break;
        case 'email':
        case 'followup':
          navigate('/messages');
          break;
        case 'task':
          // General tasks remain on dashboard
          break;
      }
    }
  };
};
